import React, { useEffect, useState } from 'react'
import styled, { keyframes, StyledProps } from 'styled-components'
import { IconCheckCircle, IconLoader, IconWarning } from '../../iconography/Icons/Icons'
import { Progress } from '../../../globalTypes'

type SpinnerSize = 'small' | 'medium' | 'big'

const rotate = keyframes`
  from  {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`

const bounce = keyframes`
  0% {
    transform: scale(0);
  }

  50% {
    transform: scale(1.2);
  }

  100% {
    transform: scale(1);
  }
`

const DefaultIcon = styled(IconLoader)`
  width: 100% !important;
  height: 100% !important;
  fill: #fff;
  &[data-filled='true'] {
    fill: #037bff;
  }
`

const CustomIcon = styled.img`
  width: 100%;
  height: 100%;
`

const Container = styled.div<StyledProps<{ size: SpinnerSize }>>`
  display: flex;
  transform-origin: 51% 51%;
  ${(props: StyledProps<{ size: SpinnerSize }>) => {
    let size
    switch (props.size) {
      case 'small':
        size = '20px'
        break
      case 'medium':
        size = '50px'
        break
      case 'big':
        size = '80px'
        break
    }
    return {
      fontSize: size,
      width: size,
      height: size,
    }
  }}
`

const IconContainer = styled.div`
  width: 100%;
  height: 100%;
  animation: ${bounce} 0.8s ease-in-out forwards;
`

const SpinnerContainer = styled(Container)`
  animation: ${rotate} 1s linear infinite;
`

const IconSuccess = styled(IconCheckCircle)`
  fill: ${props => props.theme.color.accentApproving};
`

const IconFail = styled(IconWarning)`
  fill: ${props => props.theme.color.accentNotify};
`

type PropsType = {
  size?: SpinnerSize
  className?: string
  filled?: boolean
  spinnerImageUrl?: string
}

export const Spinner: React.FC<PropsType> = ({
  size = 'medium',
  className,
  filled = true,
  spinnerImageUrl,
}: PropsType) => {
  const spinnerJsx = spinnerImageUrl ? (
    <CustomIcon src={spinnerImageUrl} alt="spinner" />
  ) : (
    <DefaultIcon data-filled={filled} />
  )
  return (
    <SpinnerContainer size={size} className={className} data-test-id={'loader'}>
      {spinnerJsx}
    </SpinnerContainer>
  )
}

export const Spinnerable: React.FC<{
  size?: SpinnerSize
  className?: string
  filled?: boolean
  progress: Progress
}> = ({ progress, children, ...spinnerProps }) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'icon'>('idle')
  useEffect(() => {
    if (progress === Progress.WORK) {
      setStatus('loading')
    }
    if ([Progress.ERROR, Progress.SUCCESS].includes(progress)) {
      setStatus('icon')
      const timeout = setTimeout(() => {
        setStatus('idle')
      }, 2500)
      return () => {
        setStatus('idle')
        clearTimeout(timeout)
      }
    }
  }, [progress])

  if (progress === Progress.ERROR && status === 'icon') {
    return (
      <Container size={spinnerProps.size || 'medium'}>
        <IconContainer>
          <IconFail />
        </IconContainer>
      </Container>
    )
  }

  if (progress === Progress.SUCCESS && status === 'icon') {
    return (
      <Container size={spinnerProps.size || 'medium'}>
        <IconContainer>
          <IconSuccess />
        </IconContainer>
      </Container>
    )
  }

  if (progress === Progress.WORK) {
    return <Spinner {...spinnerProps} />
  }

  if (status !== 'idle') {
    return <Container size={spinnerProps.size || 'medium'} />
  }

  return <>{children}</>
}
